import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { CheckCircle, Loader2, XCircle, Package, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function OfferSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [orderCount, setOrderCount] = useState(0);
  const [error, setError] = useState('');

  const offerId = searchParams.get('offer_id');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const checkStatus = async () => {
      if (!offerId || !sessionId) {
        setStatus('error');
        setError('Missing payment information');
        return;
      }

      try {
        const response = await api.getOfferCheckoutStatus(offerId, sessionId);
        
        if (response.data.status === 'paid' || response.data.status === 'completed') {
          setStatus('success');
          setOrderCount(response.data.orders_created || 0);
        } else if (response.data.status === 'pending') {
          // Keep checking
          setTimeout(checkStatus, 2000);
        } else {
          setStatus('error');
          setError('Payment was not completed');
        }
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.detail || 'Failed to verify payment');
      }
    };

    checkStatus();
  }, [offerId, sessionId]);

  return (
    <div className="min-h-screen bg-dark text-white">
      <Navbar />
      
      <main className="container mx-auto px-4 py-20">
        <motion.div
          {...fadeInUp}
          className="max-w-lg mx-auto text-center"
        >
          {status === 'loading' && (
            <div className="bg-surface border border-[#262626] rounded-2xl p-12">
              <Loader2 className="w-16 h-16 text-lime mx-auto mb-6 animate-spin" />
              <h1 className="font-heading text-2xl font-bold mb-2">Processing Your Payment</h1>
              <p className="text-[#A1A1AA]">Please wait while we confirm your negotiated bundle...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-surface border border-lime/30 rounded-2xl p-12">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', damping: 10, stiffness: 100 }}
              >
                <CheckCircle className="w-20 h-20 text-lime mx-auto mb-6" />
              </motion.div>
              <h1 className="font-heading text-3xl font-bold mb-2">Payment Successful!</h1>
              <p className="text-[#A1A1AA] mb-6">
                Your negotiated bundle has been confirmed. {orderCount > 0 && `${orderCount} orders have been created.`}
              </p>
              
              <div className="bg-surface-highlight rounded-xl p-6 mb-8">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <Package className="w-5 h-5 text-lime" />
                  <span className="font-medium">What&apos;s Next?</span>
                </div>
                <p className="text-[#A1A1AA]">
                  The artist will review your orders and start working on your custom dubplates. 
                  You can track progress in your dashboard.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/dashboard?tab=orders">
                  <Button className="btn-primary w-full sm:w-auto">
                    <Package className="w-4 h-4 mr-2" />
                    View My Orders
                  </Button>
                </Link>
                <Link to="/artists">
                  <Button variant="outline" className="border-[#262626] w-full sm:w-auto">
                    Continue Shopping
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-surface border border-red-500/30 rounded-2xl p-12">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <h1 className="font-heading text-2xl font-bold mb-2">Payment Issue</h1>
              <p className="text-[#A1A1AA] mb-6">{error}</p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/dashboard?tab=offers">
                  <Button className="btn-primary w-full sm:w-auto">
                    Back to My Offers
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="border-[#262626] w-full sm:w-auto">
                    Go Home
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
